<template>
  <div id="sitter-rating" class="container">
    <RatingApiError v-if="ratingError" class="mt-48" :ratingError="ratingError" :sitterName="sitterFirstName" />
    <section v-else-if="sitter">
      <SitterDetailsHeader
        :sitterName="sitter.name"
        :sitterPhotoUrl="sitter.photoUrl"
        :hasRating="hasRating"
        :scores="scores"
        :ratingScore="ratingScore"
      />
      <div v-if="ratingInProgress">
        <form>
          <StarRatingReview :ratingSelected="ratingSelected" :ratingScore="ratingScore" :sitterFirstName="sitterFirstName" />
          <div v-if="step === 'start_rating'">
            <PublicFeedback
              v-if="feedbackType === 'public'"
              :returnPath="returnPath"
              :sitterFirstName="sitterFirstName"
              :ratingSegmentEvent="ratingSegmentEvent"
              :score="score"
              :createReview="createReview"
              :onCancel="handleCancelReview"
              @update:modelValue="updatePublicFeedback"
            />

            <PrivateFeedback
              v-if="feedbackType === 'private'"
              :returnPath="returnPath"
              :ratingSegmentEvent="ratingSegmentEvent"
              :sitterFirstName="sitterFirstName"
              :createPrivateReview="createPrivateReview"
              :onCancel="handleCancelReview"
              @update:modelValue="updatePrivateFeedback"
            />
          </div>

          <UpdateFeedback
            v-show="step === 'public_feedback'"
            :sitterFirstName="sitterFirstName"
            :ratingSegmentEvent="ratingSegmentEvent"
            :returnPath="returnPath"
            :updateReview="updateReview"
            :onSkip="handleSkipReview"
            @update:modelValue="updatePublicFeedback"
          />
        </form>
      </div>
      <SitterRatingSuccess v-if="ratingSuccessful" :sitter="sitter" :rating-score="ratingScore" />
    </section>
    <GenericError
      v-else
      class="flex justify-center mt-48 mb-48"
      title="We couldn't find that sitter"
      buttonValue="Search for sitters"
      buttonHref="/availability/search"
      >The sitter you're looking for is no longer on Sittercity. Try searching for a new sitter or contact Customer Success if you
      have any concerns.
    </GenericError>
  </div>
</template>

<script lang="ts">
import { decodeJwt } from 'jose';
import Rating from '../../models/rating';
import { readDomainCookie } from '../../lib/domain_cookie';

import { SUBMIT_RATING_MUTATION, UPDATE_REVIEW_MUTATION } from '../../graphql/ratings/mutations';

import RatingApiError from './RatingApiError.vue';
import GenericError from '../../components/generic_error/GenericError.vue';
import SitterRatingSuccess from './SitterRatingSuccess.vue';
import StarRatingReview from './components/StarRatingReview.vue';
import PrivateFeedback from './components/PrivateFeedback.vue';
import PublicFeedback from './components/PublicFeedback.vue';
import UpdateFeedback from './components/UpdateFeedback.vue';
import SitterDetailsHeader from './components/SitterDetailsHeader.vue';
import { sendSegmentEvent } from '../../lib/segment';
import { navigateToReturnPath } from '@/helpers/routerNavigation';

const titles = {
  FIVE_STARS: 'Great',
  FOUR_STARS: 'Okay',
  THREE_STARS: 'Meh',
  TWO_STARS: 'Bad',
  ONE_STAR: 'Terrible',
};

const scores = {
  FIVE_STARS: 5,
  FOUR_STARS: 4,
  THREE_STARS: 3,
  TWO_STARS: 2,
  ONE_STAR: 1,
};

const feedback = {
  FIVE_STARS: 'public',
  FOUR_STARS: 'public',
  THREE_STARS: 'public',
  TWO_STARS: 'private',
  ONE_STAR: 'private',
};

export default {
  components: {
    RatingApiError,
    GenericError,
    SitterRatingSuccess,
    StarRatingReview,
    PrivateFeedback,
    PublicFeedback,
    UpdateFeedback,
    SitterDetailsHeader,
  },
  props: {
    sitter: {
      type: Object,
      default: null,
    },
  },
  data(): {
    privateFeedback: string;
    publicFeedback: string;
    ratingScore: string;
    ratingError: string;
    ratingInProgress: boolean;
    ratingSuccessful: boolean;
    step: string;
    rating: Rating;
  } {
    return {
      privateFeedback: '',
      publicFeedback: '',
      ratingScore: '',
      ratingError: '',
      ratingInProgress: true,
      ratingSuccessful: false,
      step: 'start_rating',
      rating: new Rating(),
    };
  },
  computed: {
    feedbackType(): string {
      return feedback[this.ratingScore];
    },
    scores(): Record<string, number> {
      return scores;
    },
    hasRating(): boolean {
      if (this.ratingSuccessful || this.step === 'public_feedback') {
        return true;
      }
      return false;
    },
    isPrivateFeedbackEmpty(): boolean {
      return this.privateFeedback.length === 0;
    },
    returnPath(): string {
      const query = this.$route.query as Record<string, any>;
      let rp = query.return_path ?? query.returnPath ?? query['return-path'];

      if (Array.isArray(rp)) rp = rp[0];
      if (typeof rp === 'string') {
        try {
          return decodeURIComponent(rp);
        } catch {
          return rp;
        }
      }
      return '/account/seekers/dashboard';
    },
    score(): number {
      return scores[this.ratingScore];
    },
    scoreTitle(): string {
      return titles[this.ratingScore];
    },
    sitterFirstName(): string {
      return this.sitter.name.slice(0, -3);
    },
    myId(): string {
      const token = readDomainCookie('jwt');
      if (!token) throw new Error('No current user');

      const { sub } = decodeJwt(token);
      if (!sub) throw new Error('No current user');

      return sub;
    },
  },
  methods: {
    updatePrivateFeedback(feedbackProp): any {
      this.privateFeedback = feedbackProp;
    },
    updatePublicFeedback(feedbackProp): any {
      this.publicFeedback = feedbackProp;
    },
    getSourceParam(): string {
      const query = window.location.search.substring(1);
      const vars = query.split('&');
      const found = vars.find((v) => v.startsWith('source='));
      if (found) return found.slice(7);
      return '';
    },
    ratingSegmentEvent(action: string) {
      sendSegmentEvent(action, {
        parent_uuid: this.myId,
        sitter_uuid: this.sitter.id,
        rating_uuid: this.rating.UUID,
        source_page: 'sitter_profile',
        source_button: this.getSourceParam(),
      });
    },
    ratingSelected(rating: string): any {
      this.ratingScore = rating;
      sendSegmentEvent('Rating Selected', {
        parent_uuid: this.myId,
        sitter_uuid: this.sitter.id,
        rating_uuid: this.rating.UUID,
        rating: this.score,
      });
    },
    handleCancelReview(): void {
      navigateToReturnPath(this.$router, '/account/seekers/dashboard');
    },
    handleSkipReview(): void {
      navigateToReturnPath(this.$router, '/account/seekers/dashboard');
    },
    createPrivateReview(): any {
      this.$apollo
        .mutate({
          mutation: SUBMIT_RATING_MUTATION,
          variables: {
            id: this.rating.UUID,
            stars: this.ratingScore,
            reviewee: this.sitter.id,
            publicFeedback: this.publicFeedback,
            privateFeedback: this.privateFeedback,
          },
        })
        .then(() => {
          this.step = 'public_feedback';
          this.ratingSegmentEvent('Rating Submitted');
          this.snapToTop();
        })
        .catch((error) => {
          this.handleReviewError(error);
        });
    },
    createReview(): any {
      this.$apollo
        .mutate({
          mutation: SUBMIT_RATING_MUTATION,
          variables: {
            id: this.rating.UUID,
            stars: this.ratingScore,
            reviewee: this.sitter.id,
            publicFeedback: this.publicFeedback,
            privateFeedback: this.privateFeedback,
          },
        })
        .then(() => {
          this.ratingSuccessful = true;
          this.ratingInProgress = false;
          this.ratingSegmentEvent('Rating Submitted');
        })
        .catch((error) => {
          this.handleReviewError(error);
        });
    },
    updateReview(): any {
      this.$apollo
        .mutate({
          mutation: UPDATE_REVIEW_MUTATION,
          variables: {
            id: this.rating.UUID,
            stars: this.ratingScore,
            publicFeedback: this.publicFeedback,
          },
        })
        .then(() => {
          this.ratingSuccessful = true;
          this.ratingInProgress = false;
          this.ratingSegmentEvent('Review Submitted');
        })
        .catch((error) => {
          this.handleReviewError(error);
        });
    },
    snapToTop(): any {
      if (document.body && document.documentElement) {
        document.documentElement.scrollTop = 0;
        document.body.scrollTop = 0;
      }
    },
    handleReviewError(error): any {
      this.ratingError = error.message === 'duplicate review id' ? 'duplicate_review' : 'internal_server';
      this.ratingInProgress = false;
    },
  },
};
</script>
